import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '@bliks-io/ui';
import { profileQueryOptions } from '@/api/queries/useProfile';
import { resolveLockRedirect } from '@/lib/auth/resolveLockRedirect';
import { LOCK_ROUTE_SEGMENTS } from '@/constants/routes';

function isLockScreenPath(): boolean {
  return LOCK_ROUTE_SEGMENTS.some((segment) => window.location.pathname.includes(segment));
}

/**
 * Boot-time skeleton, shown while the profile loads before the router can
 * render. The lock screens (Ersteinrichtung wizard, waiting page, seat
 * overrun) live outside the app chrome but mount their own Header, so
 * reloading them must not flash a sidebar that the destination does not have.
 */
export function AppBootSkeleton() {
  return isLockScreenPath() ? <LockScreenSkeleton /> : <AppLayoutSkeleton />;
}

/**
 * The $slug pending UI. Sign-in enters on the bare slug, and only the profile
 * the guard is still fetching tells whether the org is locked, so the sidebar
 * shows only once the cached profile resolves to a usable org.
 */
export function SlugPendingSkeleton() {
  const { data: profile } = useQuery({ ...profileQueryOptions, enabled: false });
  const isUsableOrg = profile !== undefined && resolveLockRedirect(profile) === null;
  return isUsableOrg && !isLockScreenPath() ? <AppLayoutSkeleton /> : <LockScreenSkeleton />;
}

function LockScreenSkeleton() {
  return (
    <div className="flex h-screen flex-col">
      <header className="flex h-14 shrink-0 items-center bg-sidebar px-4 w-full">
        <div className="flex items-center flex-1">
          <Skeleton className="w-24 h-6" />
        </div>
        <Skeleton className="w-9 h-9 rounded-full" />
      </header>
      <div className="flex min-h-0 flex-1 items-center justify-center bg-surface-subtle">
        <div className="w-full max-w-2xl space-y-4 p-8">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-96" />
          <Skeleton className="mt-8 h-64 w-full rounded-lg" />
        </div>
      </div>
    </div>
  );
}

export function AppLayoutSkeleton() {
  return (
    <div className="flex flex-col h-screen bg-sidebar">
      {/* Header Skeleton */}
      <header className="flex h-14 shrink-0 items-center bg-sidebar px-4 w-full">
        <div className="flex items-center flex-1 gap-2">
          {/* Logo skeleton */}
          <Skeleton className="w-24 h-6" />

          {/* Breadcrumb skeleton */}
          <div className="hidden ml-2 md:flex items-center gap-2">
            <Skeleton className="w-12 h-4" />
            <span className="text-muted-foreground">/</span>
            <Skeleton className="w-20 h-4" />
          </div>
        </div>

        {/* Right section skeleton */}
        <div className="flex items-center gap-2">
          {/* Notification bell skeleton */}
          <Skeleton className="w-9 h-9 rounded-md" />

          {/* User avatar skeleton */}
          <Skeleton className="w-9 h-9 rounded-full" />
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        {/* Sidebar Skeleton */}
        <nav className="flex flex-col w-14 bg-sidebar text-sidebar-foreground shrink-0">
          <div className="flex-1 overflow-y-auto p-2">
            <div className="flex flex-col gap-2">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
                <Skeleton key={i} className="w-10 h-10 rounded-md" />
              ))}
            </div>
          </div>

          {/* Footer skeleton */}
          <div className="p-2">
            <Skeleton className="w-10 h-10 rounded-md" />
          </div>
        </nav>

        {/* Main content skeleton */}
        <main className="flex-1 mb-4 mr-4 overflow-auto border bg-background rounded-2xl border-border p-10">
          <div className="space-y-4">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-96" />
            <Skeleton className="h-4 w-80" />
            <div className="mt-8 space-y-4">
              <Skeleton className="h-32 w-full rounded-lg" />
              <Skeleton className="h-32 w-full rounded-lg" />
              <Skeleton className="h-32 w-full rounded-lg" />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
